import { useState } from 'react';
import { FileClock } from 'lucide-react';
import { useFormat } from '@/hooks/useAccess';
import { useAuditLogsQuery } from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDateTime } from '@/lib/format';
import {
  Alert, Badge, Card, EmptyState, ErrorState, Input, LoadingState, PageHeader,
  Pagination, Select, Table, Td, Th,
} from '@/components/ui';

const ACTIONS = [
  'LOGIN', 'LOGIN_FAILED', 'LOGOUT', 'CREATE', 'UPDATE', 'DELETE', 'APPROVE', 'CANCEL',
  'REFUND', 'PERMISSION_CHANGED', 'MEMBER_INVITED', 'MEMBER_REMOVED', 'STOCK_ADJUSTED',
  'PASSWORD_CHANGED', 'TOKEN_REUSE_DETECTED',
];

const TONE: Record<string, 'success' | 'danger' | 'warning' | 'info' | 'neutral'> = {
  CREATE: 'success',
  UPDATE: 'info',
  DELETE: 'danger',
  LOGIN: 'neutral',
  LOGIN_FAILED: 'warning',
  TOKEN_REUSE_DETECTED: 'danger',
  PERMISSION_CHANGED: 'warning',
  STOCK_ADJUSTED: 'warning',
  REFUND: 'warning',
};

export function AuditPage() {
  const format = useFormat();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [action, setAction] = useState('');
  const [entityType, setEntityType] = useState('');

  const { data, isLoading, isError, error, refetch } = useAuditLogsQuery({
    page,
    limit,
    action: action || undefined,
    entityType: entityType || undefined,
  });

  return (
    <>
      <PageHeader
        title="Audit log"
        description="Every significant action, with who did it and what changed."
      />

      <div className="mb-4">
        <Alert tone="info">
          Audit entries are append-only. Nobody — including owners — can edit or delete them
          from the application.
        </Alert>
      </div>

      <Card>
        <div className="flex flex-wrap items-end gap-3 border-b border-subtle p-4">
          <div className="w-full sm:w-56">
            <Select label="Action" value={action} onChange={(event) => { setAction(event.target.value); setPage(1); }}>
              <option value="">All actions</option>
              {ACTIONS.map((entry) => (
                <option key={entry} value={entry}>{entry.toLowerCase().replaceAll('_', ' ')}</option>
              ))}
            </Select>
          </div>
          <div className="w-full sm:w-56">
            <Input
              label="Entity type"
              placeholder="Product, Sale, Role…"
              value={entityType}
              onChange={(event) => { setEntityType(event.target.value); setPage(1); }}
            />
          </div>
        </div>

        {isLoading ? (
          <LoadingState />
        ) : isError ? (
          <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
        ) : data && data.items.length === 0 ? (
          <EmptyState icon={FileClock} title="No matching audit entries" />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>When</Th>
                  <Th>Who</Th>
                  <Th>Action</Th>
                  <Th>Entity</Th>
                  <Th>Changed</Th>
                  <Th>IP</Th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map((entry) => (
                  <tr key={entry.id} className="hover:bg-sunken">
                    <Td className="whitespace-nowrap text-xs text-secondary">
                      {formatDateTime(entry.occurredAt, format)}
                    </Td>
                    <Td>
                      {entry.user ? (
                        <>
                          <p className="text-sm font-medium">
                            {entry.user.firstName} {entry.user.lastName}
                          </p>
                          <p className="text-xs text-muted">{entry.user.email}</p>
                        </>
                      ) : (
                        <span className="text-sm text-muted">System</span>
                      )}
                    </Td>
                    <Td>
                      <Badge tone={TONE[entry.action] ?? 'neutral'}>
                        {entry.action.toLowerCase().replaceAll('_', ' ')}
                      </Badge>
                    </Td>
                    <Td>
                      <p className="text-sm">{entry.entityType}</p>
                      {entry.entityLabel && <p className="text-xs text-muted">{entry.entityLabel}</p>}
                    </Td>
                    <Td className="text-xs text-secondary">
                      {entry.changedFields.length > 0 ? entry.changedFields.join(', ') : '—'}
                    </Td>
                    <Td className="font-mono text-xs text-muted">{entry.ipAddress ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {data && (
              <Pagination
                page={data.meta.page}
                totalPages={data.meta.totalPages}
                total={data.meta.total}
                onPageChange={setPage}
                limit={limit}
                onLimitChange={(next) => {
                  setLimit(next);
                  setPage(1);
                }}
              />
            )}
          </>
        )}
      </Card>
    </>
  );
}
